/**
 * CHANGES IN A PHASE (2026-10-10, docs/bim-plan/32 §3 B; Matt's answer to question 2: "the
 * drawing phase decides"). One existing element changed in a later phase without being remodeled:
 * an existing door gets new hardware in New Construction, a run of existing cabinets new fronts.
 * The element keeps one record; the change rides on it as instance param `phaseChanges`:
 *
 *   phaseChanges: [{ phase: <PhaseId>, params: { <key>: <value> | UNSET } }]
 *
 * - Resolved as of phase P, the element is its own params plus every change made at or before P,
 *   applied in phase order. The Existing view shows the before state, New Construction the after.
 * - `UNSET` (`{ $unset: true }`) means "back to following the type / project" at that phase,
 *   which a stored null does not (a stored null resolves as null).
 * - EDIT ROUTING: the app sets the session's EDIT PHASE (its drawing phase) with `setEditPhase`.
 *   A param write to an element that stands EXISTING in that phase (built earlier, not yet
 *   demolished) is recorded as a change in that phase; a write in the element's own phase
 *   corrects the element itself. Placement and geometry params (the schema's `positional`,
 *   paths, levels) always write the element itself: moving an existing wall is demolition plus
 *   new work, not a change. Headless callers that never set an edit phase write as before.
 *
 * Pure apart from the edit-phase registry (per Y.Doc, session state, never stored in the doc).
 */
import type { ParamValue, PhaseRecord } from './types'

export const PHASE_CHANGES_KEY = 'phaseChanges'
export const UNSET = Object.freeze({ $unset: true }) as unknown as ParamValue

/** EAVES IN A PHASE (2026-10-30, Matt: "say what they are set to in existing phase and what we
 *  want them changed to in the new construction"): a change may also carry per-EDGE attrs of a
 *  path param, keyed by the path's param key, then the edge's FROM node id (the node that holds
 *  `edge` attrs for the segment to the next node). Node ids are stable through edits, so a
 *  change stays on its edge when another node is added or moved. */
export type EdgeChanges = Record<string, Record<string, Record<string, ParamValue>>>
export interface PhaseChange { phase: string; params: Record<string, ParamValue>; edges?: EdgeChanges }

export const isUnset = (v: unknown): boolean =>
  !!v && typeof v === 'object' && (v as Record<string, unknown>).$unset === true

function normalizeEdges(raw: unknown): EdgeChanges | null {
  if (!raw || typeof raw !== 'object') return null
  const out: EdgeChanges = {}
  for (const [pk, byNode] of Object.entries(raw as Record<string, unknown>)) {
    if (!byNode || typeof byNode !== 'object') continue
    for (const [nid, attrs] of Object.entries(byNode as Record<string, unknown>)) {
      if (!attrs || typeof attrs !== 'object' || !Object.keys(attrs).length) continue
      ;(out[pk] ??= {})[nid] = { ...(attrs as Record<string, ParamValue>) }
    }
  }
  return Object.keys(out).length ? out : null
}

/** a path param with the edge changes laid over its nodes' `edge` attrs */
function pathWithEdges(path: unknown, byNode: Record<string, Record<string, ParamValue>>): unknown {
  const p = path as { nodes?: { id?: string; edge?: Record<string, unknown> }[] } | null
  if (!p || !Array.isArray(p.nodes)) return path
  let hit = false
  const nodes = p.nodes.map((n) => {
    const ch = n.id ? byNode[n.id] : undefined
    if (!ch) return n
    hit = true
    const edge: Record<string, unknown> = { ...(n.edge ?? {}) }
    for (const [k, v] of Object.entries(ch)) {
      if (isUnset(v) || v === null) delete edge[k]
      else edge[k] = v
    }
    const { edge: _e, ...bare } = n
    return Object.keys(edge).length ? { ...bare, edge } : bare
  })
  return hit ? { ...p, nodes } : path
}

export function normalizePhaseChanges(raw: unknown): PhaseChange[] {
  if (!Array.isArray(raw)) return []
  const out: PhaseChange[] = []
  for (const r of raw) {
    const c = (r || {}) as Partial<PhaseChange>
    if (typeof c.phase !== 'string' || !c.phase || !c.params || typeof c.params !== 'object') continue
    const params = { ...(c.params as Record<string, ParamValue>) }
    const edges = normalizeEdges((c as { edges?: unknown }).edges)
    if (Object.keys(params).length || edges) out.push({ phase: c.phase, params, ...(edges ? { edges } : {}) })
  }
  return out
}

const orderOf = (phases: PhaseRecord[], id: string | null | undefined): number | null => {
  if (!id) return null
  const p = phases.find((ph) => ph.id === id)
  return p ? p.order : null
}
const lastOrder = (phases: PhaseRecord[]): number => phases.reduce((m, p) => Math.max(m, p.order), -Infinity)

/** the element's stored params as of `phaseId` (null = the model's last phase). A change whose
 *  phase was deleted never applies. Returns `params` itself when nothing applies. */
export function paramsAtPhase(
  params: Record<string, ParamValue>, phases: PhaseRecord[], phaseId: string | null | undefined,
): Record<string, ParamValue> {
  const changes = normalizePhaseChanges(params[PHASE_CHANGES_KEY])
  if (!changes.length || !phases.length) return params
  const view = orderOf(phases, phaseId) ?? lastOrder(phases)
  const apply = changes
    .map((c) => ({ c, o: orderOf(phases, c.phase) }))
    .filter((x): x is { c: PhaseChange; o: number } => x.o != null && x.o <= view)
    .sort((a, b) => a.o - b.o)
  if (!apply.length) return params
  const out = { ...params }
  for (const { c } of apply) {
    for (const [k, v] of Object.entries(c.params)) {
      if (isUnset(v)) delete out[k]
      else out[k] = v
    }
    for (const [pk, byNode] of Object.entries(c.edges ?? {})) out[pk] = pathWithEdges(out[pk], byNode) as ParamValue
  }
  return out
}

/** the latest phase at or before `phaseId` that any change in the model is recorded in — part of
 *  the read cache's signature (two phases with no change between them resolve alike) */
export function changePhaseKey(changePhases: Set<string>, phases: PhaseRecord[], phaseId: string | null | undefined): string {
  if (!changePhases.size) return ''
  const view = orderOf(phases, phaseId) ?? lastOrder(phases)
  let best: string | null = null
  let bestO = -Infinity
  for (const id of changePhases) {
    const o = orderOf(phases, id)
    if (o != null && o <= view && o > bestO) { best = id; bestO = o }
  }
  return `pc:${best ?? '-'}`
}

/** a change list with `key` set (or removed, `value` undefined) in `phase` */
export function withChange(
  changes: PhaseChange[], phase: string, key: string, value: ParamValue | undefined,
): PhaseChange[] {
  const out: PhaseChange[] = changes.map((c) => ({ phase: c.phase, params: { ...c.params }, ...(c.edges ? { edges: c.edges } : {}) }))
  let entry = out.find((c) => c.phase === phase)
  if (!entry) {
    if (value === undefined) return out
    entry = { phase, params: {} }
    out.push(entry)
  }
  if (value === undefined) delete entry.params[key]
  else entry.params[key] = value
  return out.filter(hasContent)
}

const hasContent = (c: PhaseChange) => Object.keys(c.params).length > 0 || !!(c.edges && Object.keys(c.edges).length)

/** a change list with one edge attr set in `phase` (`value` undefined = drop that recorded
 *  attr, back to the edge as it exists; UNSET = the attr removed from that phase on) */
export function withEdgeChange(
  changes: PhaseChange[], phase: string, pathKey: string, nodeId: string, key: string, value: ParamValue | undefined,
): PhaseChange[] {
  const out = changes.map((c) => ({
    phase: c.phase, params: { ...c.params },
    ...(c.edges ? { edges: Object.fromEntries(Object.entries(c.edges).map(([pk, bn]) => [pk, Object.fromEntries(Object.entries(bn).map(([n, a]) => [n, { ...a }]))])) } : {}),
  })) as PhaseChange[]
  let entry = out.find((c) => c.phase === phase)
  if (!entry) {
    if (value === undefined) return out
    entry = { phase, params: {} }
    out.push(entry)
  }
  const edges = (entry.edges ??= {})
  const byNode = (edges[pathKey] ??= {})
  const attrs = (byNode[nodeId] ??= {})
  if (value === undefined) delete attrs[key]
  else attrs[key] = value
  if (!Object.keys(attrs).length) delete byNode[nodeId]
  if (!Object.keys(byNode).length) delete edges[pathKey]
  if (!Object.keys(edges).length) delete entry.edges
  return out.filter(hasContent)
}

// ---------- the session's edit phase ----------
const editPhases = new WeakMap<object, string | null>()
/** the app's drawing phase: writes to an element standing existing in it become changes */
export function setEditPhase(doc: object, phaseId: string | null): void {
  editPhases.set(doc, phaseId)
}
export function getEditPhase(doc: object): string | null {
  return editPhases.get(doc) ?? null
}
